import { useEffect, useState } from 'react';
import { ConversationPromptStatus, MessageMedia, ReferenceLists, ReferenceListTypes } from 'utils/gloo-types';
import { toast } from 'react-toastify';
import {
  createConversationPrompt,
  getAllSuggestedTopics,
  getReferenceListValuesByType,
  setData,
  updateConversationPrompt,
} from 'actions/requests';
import { convertReferenceListToSelectOptions } from 'utils';
import { useThunkDispatch } from 'utils/reduxHook';
import { useIsMobile } from 'utils/hooks';
import Typography from 'components/atoms/Typography';
import { CloudinaryMediaUploader, MediaUploadResult } from 'components/organisms/CloudinaryMediaUploader';
import { MMSMediaInput } from 'components/molecules/MMSMediaInput';
import SelectTopicTag from 'components/molecules/SelectTopicTag';
import Button from 'components/atoms/Button';
import SuggestedTopics from 'components/molecules/SuggestedTopics';
import Input from 'components/molecules/Input';
import { formatMMSMedia } from 'utils/mms';
import CloudinaryVideoPlayer from 'components/organisms/CloudinaryVideoPlayer';
import PreviewYourPrompt from '../PreviewYourPrompt';

const videoMaxSize = { size: 4 * 1000 * 1024 * 1024, label: '4 GB' };

interface ConversationPromptFormProps {
  handleFormChange: (field, value) => void;
  formData: {
    id?: string;
    name: string;
    message: string;
    promptTip: string;
    videoUrl?: string;
    authorImageUrl?: string;
    author: string;
    topicsArr: Partial<ReferenceLists[]>;
    status: ConversationPromptStatus;
    messageMedia: MessageMedia[];
  };
  isEditing: boolean;
  setFormData: (ConversationPrompt) => void;
  isLoading?: boolean;
  handleFormPostSubmit: () => void;
}

function ConversationPromptForm({
  handleFormChange,
  formData,
  isEditing,
  setFormData,
  isLoading,
  handleFormPostSubmit,
}: ConversationPromptFormProps) {
  const [invalid, setInvalid] = useState(true);
  const dispatch = useThunkDispatch();
  const canBeSavedAsDraft = !isEditing || formData.status === 'Draft';
  const characterLimit = 1224;
  const isOverLimit = formData.message.length > characterLimit;
  const isMobile = useIsMobile();

  const formatTopicsArr = (options) => {
    return options.map(({ value, label }) => {
      return { title: label, value };
    });
  };

  const isSubmittable = () => {
    setInvalid(!formData.name || !formData.topicsArr?.length || !formData.message || !formData.author);
  };

  const handleFormSubmit = async (status) => {
    try {
      if (isEditing) {
        await dispatch(updateConversationPrompt(formData.id, { ...formData, status }));
        toast.success('Conversation Prompt Updated Successfully');
      } else {
        await dispatch(createConversationPrompt({ ...formData, status }));
        toast.success('Conversation Prompt Created Successfully');
      }
      handleFormChange('status', status);
      handleFormPostSubmit();
    } catch (error) {
      toast.error(error);
    }
  };

  const getAllTopics = async () => {
    await dispatch(getReferenceListValuesByType(ReferenceListTypes.TOPICS, 'true'));
  };

  const getSuggestedTopics = async () => {
    const suggestedTopics = await dispatch(getAllSuggestedTopics());

    const filteredTopics = suggestedTopics.filter((suggestedTopic) => {
      return !formData.topicsArr.map((selectedTopic) => selectedTopic.value).includes(suggestedTopic.value);
    });

    dispatch(setData('suggestedTopics', filteredTopics));
  };

  const handleAddSuggestedTopic = ({ title, value }) => {
    const topicAlreadyAdded = formData.topicsArr.some((t) => t.value === value);
    if (formData.topicsArr.length < 5) {
      setFormData({
        ...formData,
        topicsArr: [...formData.topicsArr, ...(!topicAlreadyAdded ? [{ title, value }] : [])],
      });
    } else if (!topicAlreadyAdded) {
      toast.error('You can only add up to 5 topics');
    }
  };

  useEffect(() => {
    getAllTopics();
    if (!isEditing) {
      delete formData.id;
    }
  }, []);

  useEffect(() => {
    isSubmittable();
    getSuggestedTopics();
  }, [formData]);

  return (
    <div>
      {!isLoading && (
        <>
          <div className="flex flex-col pt-4 flex-1 mx-1">
            <Typography variant="label" className="py-3 text-glooCharcoal">
              Message Name
            </Typography>
            <Input
              autoFocus
              className="text-neutral-800 font-sm"
              placeholder="Name Your Message"
              onChangeText={(t) => handleFormChange('name', t)}
              value={formData.name}
            />
          </div>
          <div className="flex flex-col pt-4 flex-1 mx-1">
            <Typography variant="label" className="py-3 text-glooCharcoal">
              Message Content
            </Typography>
            <MMSMediaInput
              autoFocus
              value={formData.message}
              onChangeText={(t) => handleFormChange('message', t)}
              messageMedia={formData.messageMedia?.[0]}
              onMediaUpload={(mediaRef: MediaUploadResult) => {
                setFormData({
                  ...formData,
                  messageMedia: formatMMSMedia(mediaRef),
                });
              }}
              characterLimit={characterLimit}
              placeholder="Input Text Here"
              removeImageFromLocalState={() => {
                setFormData({
                  ...formData,
                  messageMedia: [],
                });
              }}
              onShortenUrl={(t) => handleFormChange('message', t)}
              onAddUserData={(userData) => {
                setFormData({
                  ...formData,
                  message: userData,
                });
              }}
              className="text-neutral-800 font-sm"
              textReplacerType="Conversation Prompts"
            />
            <div className="pt-2 font-sans text-sm text-glooCharcoal">
              This will be the message inserted in your thread.
            </div>
          </div>
          <div className="flex flex-col pt-4 flex-1 mx-1">
            <div className="flex flex-row justify-start file:py-3">
              <Typography variant="label" className="py-3 text-glooCharcoal">
                Message Tip
              </Typography>
              <div className="text-sm font-normal pl-1 py-3 text-glooCharcoal">(Optional)</div>
            </div>
            <Input
              className="text-neutral-800 font-sm"
              placeholder="Input Text here"
              value={formData.promptTip}
              onChangeText={(t) => handleFormChange('promptTip', t)}
            />
            <div className="pt-2 font-sans text-sm text-glooCharcoal">
              This will be the content inserted in the message.
            </div>
          </div>
          <div className="flex flex-col pt-4 flex-1 mx-1">
            <Typography variant="label" className="py-3 text-glooCharcoal">
              Message Author
            </Typography>
            <Input
              className="text-neutral-800 font-sm"
              placeholder="Author Name Here"
              value={formData.author}
              onChangeText={(t) => handleFormChange('author', t)}
            />

            <Typography variant="label" className="py-3 text-glooCharcoal">
              Author Image
            </Typography>
            <div className="flex flex-col items-start">
              {formData.authorImageUrl && (
                <img
                  src={formData.authorImageUrl}
                  alt={formData.author}
                  className="bg-white w-20 h-20 rounded-full my-2"
                />
              )}
              <CloudinaryMediaUploader
                icon="image"
                iconSize={20}
                text="Choose Image"
                className="text-glooMedGray hover:text-skyBlue-600"
                onMediaUpload={(mediaUploadResult) => {
                  if (mediaUploadResult?.info?.path) {
                    setFormData({
                      ...formData,
                      authorImageUrl: `http://res.cloudinary.com/thryve/image/upload/c_limit,h_100,w_100/${mediaUploadResult.info.path}`,
                    });
                  }
                }}
                uploaderOptions={{ clientAllowedFormats: ['png', 'jpeg', 'bmp', 'gif'] }}
              />
            </div>
          </div>
          <div className="flex flex-col pt-4 flex-1 mx-1">
            <Typography variant="label" className="py-3 text-glooCharcoal">
              Topics
            </Typography>
            <SelectTopicTag
              onCreate={({ label, value }) => {
                if (formData.topicsArr.length < 5) {
                  setFormData({
                    ...formData,
                    topicsArr: [...formData.topicsArr, { title: label, value }],
                  });
                  return;
                }
                toast.error('You can only add up to 5 topics');
              }}
              selectedTopics={convertReferenceListToSelectOptions(formData.topicsArr)}
              onChange={(options, actionMeta) => {
                if (actionMeta.action === 'select-option' && formData.topicsArr.length >= 5) {
                  toast.error('You can only add up to 5 topics');
                  return;
                }
                setFormData({ ...formData, topicsArr: formatTopicsArr(options) });
              }}
            />
            <SuggestedTopics handleAddSuggestedTopic={handleAddSuggestedTopic} />
          </div>

          <div className="flex flex-col items-start pt-4 flex-1 mx-1">
            <Typography variant="label" className="py-3 text-glooCharcoal">
              Video (optional)
            </Typography>
            <Typography variant="sm" className="mb-4">
              Some things to keep in mind when uploading a video:
              <ul className="list-disc list-inside">
                <li>
                  Make sure your video is less than <strong>{videoMaxSize.label}</strong>.
                </li>
                <li>A 16:9 video aspect ratio is preferred</li>
                <li>This gets attached as a shortened URL when sending out the message</li>
              </ul>
            </Typography>
            <CloudinaryMediaUploader
              buttonType="button"
              text={formData.videoUrl ? 'Choose a different video' : 'Choose Video to Upload'}
              helpText="Upload Video"
              type="primary"
              icon="video"
              iconSize={20}
              onMediaUpload={(mediaUploadResult) => {
                if (mediaUploadResult?.info?.playback_url) {
                  handleFormChange('videoUrl', mediaUploadResult.info.playback_url);
                }
              }}
              uploaderOptions={{
                clientAllowedFormats: ['video'],
                maxFileSize: videoMaxSize.size,
                resourceType: 'video',
              }}
            />
            {formData.videoUrl && (
              <div>
                <Typography variant="label" className="mt-2">
                  Uploaded Url: {formData.videoUrl}
                </Typography>
                <Typography variant="label" className="py-3">
                  Preview Video
                </Typography>
                <CloudinaryVideoPlayer url={formData.videoUrl} />
              </div>
            )}
          </div>
        </>
      )}
      {isMobile && <PreviewYourPrompt isLoading={isLoading} prompt={formData} />}
      <div className="flex flex-row justify-end">
        {canBeSavedAsDraft && (
          <div className="pt-4 px-1">
            <Button
              onClick={() => handleFormSubmit('Draft')}
              type="tertiary"
              text="Save as Draft"
              disabled={isLoading || isOverLimit}
              dataDurableId="saveAsDraftConversationPromptButton"
            />
          </div>
        )}
        {formData.status === 'Unpublished' && (
          <div className="pt-4 px-1">
            <Button
              onClick={() => handleFormSubmit('Unpublished')}
              type="tertiary"
              text="Save as Not Published"
              disabled={isLoading || invalid}
              dataDurableId="saveAsUnpublishedConversationPromptButton"
            />
          </div>
        )}
        <div className="pt-4 px-1">
          <Button
            onClick={() => handleFormSubmit('Published')}
            type="navy"
            text="Publish"
            disabled={isLoading || invalid || isOverLimit}
            dataDurableId="publishConversationPromptButton"
          />
        </div>
      </div>
    </div>
  );
}

export default ConversationPromptForm;
